import { ButtonProps, Divider, Input } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaModal, RanaTable } from "@nens/ranalib";
import { listToString } from "@nens/shared-utils";

import { FoldersBreadcrumb } from "@/components/folders-breadcrumb";
import { TableEmptyFiles } from "@/components/table-empty-file";
import { RanaFileIcon, getDisplayType } from "@/lib/rana-file-data-type";
import { cancelButtonText } from "@/translations";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";
import { containsInvalidCharacters, getFirstInvalidCharacter } from "@/utils/invalid-characters";

import { useProcessRunFormFileModal } from "../hooks/use-process-run-form-file-modal";
import { ProcessRunFormFileModalProps } from "../types";
import { FileModalMode, isDirectoryMode } from "../utils/file-modal-mode";
import { getAcceptedDataTypes } from "../utils/get-accepted-data-types";

const useSubmitButtonProps = ({
  paths,
  fileName,
  mode,
}: {
  paths: string[];
  fileName: string | undefined;
  mode: FileModalMode;
}): Pick<ButtonProps, "isDisabled" | "title" | "children"> => {
  const intl = useIntl();
  if (!fileName && mode === "output-file") {
    return {
      isDisabled: true,
      title: intl.formatMessage(
        {
          id: "process.no_file_or_directory_name_give",
          defaultMessage: "No {isDirectory, select, true {folder} other {file}} name given",
        },
        { isDirectory: false }
      ),
    };
  }

  if (fileName && containsInvalidCharacters(fileName)) {
    return {
      isDisabled: true,
      title: intl.formatMessage(
        {
          id: "foldererrors.not_allowed_character",
          defaultMessage: `Character "{character}" is not allowed`,
        },
        {
          character: getFirstInvalidCharacter(fileName),
        }
      ),
    };
  }

  if (mode === "output-directory" && !fileName) {
    const isSavingInRoot = paths.length === 0;

    if (isSavingInRoot) {
      return {
        isDisabled: true,
        title: intl.formatMessage({
          id: "process.save_in_root",
          defaultMessage: "Cannot save in root folder",
        }),
      };
    }

    const isSavingHere = paths.length > 0;

    if (isSavingHere) {
      return {
        children: intl.formatMessage({
          id: "process.save_here",
          defaultMessage: "Save here",
        }),
      };
    }
  }

  return {};
};

export function OutputDestinationNameModal(props: ProcessRunFormFileModalProps) {
  const { onClose, mode, schema } = props;
  const isDirectory = isDirectoryMode(mode);

  const {
    paths,
    setPaths,
    setFileName,
    fileName,
    intl,
    files,
    tableDataIds,
    isLoading,
    onDirectoryOrFileClick,
    saveChanges,
  } = useProcessRunFormFileModal(props);

  const { acceptedDataTypes, acceptedFeatureTypes } = getAcceptedDataTypes(schema);

  const submitButtonProps = useSubmitButtonProps({ fileName, mode, paths });

  const handleFileOrDirectorySelection = (fileOrDirectory: (typeof files)[number]) => {
    if (isDirectory && fileOrDirectory.type === "directory") {
      // A selected directory is the destination; discard any entered name.
      setFileName("");
    }
    onDirectoryOrFileClick(fileOrDirectory);
  };

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={
        <FormattedMessage
          id="heading.enter_a_file_or_directory_name"
          defaultMessage="Enter a {types} {isDirectory, select, true {directory} other {file}} name"
          values={{
            types: listToString(
              (acceptedFeatureTypes.length ? acceptedFeatureTypes : acceptedDataTypes).map(
                getDisplayType
              ),
              "or"
            ).toLowerCase(),
            isDirectory,
          }}
        />
      }
      maxW="xl"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          onClick: onClose,
        },
        {
          variant: "primary",
          children: intl.formatMessage({ id: "process.save_as", defaultMessage: "Save as" }),
          ...submitButtonProps,
          onClick: () => {
            onClose();
            saveChanges();
          },
        },
      ]}
    >
      <Input
        placeholder={intl.formatMessage(
          {
            id: "process.file_or_directory_name_here",
            defaultMessage: "{isDirectory, select, true {Folder} other {File}} name here",
          },
          { isDirectory }
        )}
        value={fileName}
        onChange={(e) => setFileName(e.target.value)}
      />

      <Divider mt={4} mb={4} />
      <FoldersBreadcrumb paths={paths} fileName={fileName} setPaths={setPaths} isViewingFolder />

      <RanaTable
        emptyElement={<TableEmptyFiles />}
        columnDefinitions={[
          {
            width: "table_cell",
            title: intl.formatMessage(
              {
                id: "column.file_or_directory_name",
                defaultMessage: "{isDirectory, select, true {Folder} other {File}} name",
              },
              { isDirectory }
            ),
          },
        ]}
        tableDataIds={tableDataIds}
        isSkeleton={isLoading}
      >
        <RanaTable.Container mt={4}>
          <RanaTable.Body>
            {files.map((fileOrDirectory) => {
              const { type } = fileOrDirectory;
              const name = getNameFromFileId(fileOrDirectory.id);

              return (
                <RanaTable.Row
                  onClick={() => handleFileOrDirectorySelection(fileOrDirectory)}
                  isDisabled={type !== "directory"}
                  key={fileOrDirectory.id}
                  rowId={fileOrDirectory.id}
                >
                  <RanaTable.Cell title={name}>
                    <RanaFileIcon fileOrDirectory={fileOrDirectory} />
                    {name}
                  </RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
    </RanaModal>
  );
}
